import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Gift, Trophy, Copy, Check, PartyPopper } from 'lucide-react';
import { Prize } from '@/hooks/usePrizes';
import { Confetti } from '@/components/ui/confetti';
import { toast } from 'sonner';

interface ClaimPrizeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prize: Prize | null;
  onClaim: () => Promise<{ success: boolean; claimCode: string | null }>;
  formatMonthYear: (monthYear: string) => string;
}

export function ClaimPrizeModal({
  open,
  onOpenChange,
  prize,
  onClaim,
  formatMonthYear,
}: ClaimPrizeModalProps) {
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimCode, setClaimCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const handleClaim = async () => {
    setClaiming(true);
    const result = await onClaim();
    setClaiming(false);
    
    if (result.success && result.claimCode) {
      setClaimed(true);
      setClaimCode(result.claimCode);
      setShowConfetti(true);
      toast.success('¡Premio reclamado exitosamente!');
    } else {
      toast.error('Error al reclamar el premio');
    }
  };

  const handleCopy = () => {
    if (claimCode) {
      navigator.clipboard.writeText(claimCode);
      setCopied(true);
      toast.success('Código copiado');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after animation
    setTimeout(() => {
      setClaimed(false);
      setClaimCode(null);
      setShowConfetti(false);
    }, 300);
  };

  if (!prize) return null;

  return (
    <>
      <Confetti isActive={showConfetti} duration={5000} />
      
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 border-amber-500/30 max-w-md">
          <AnimatePresence mode="wait">
            {!claimed ? (
              <motion.div
                key="claim"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="text-center py-6"
              >
                {/* Trophy Animation */}
                <motion.div
                  animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, -5, 5, 0],
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity,
                    ease: "easeInOut" 
                  }}
                  className="inline-block mb-6"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-amber-500/30 blur-2xl rounded-full" />
                    <Trophy className="h-20 w-20 text-amber-400 relative z-10" />
                  </div>
                </motion.div>

                <h2 className="text-2xl font-bold text-white mb-2">
                  🎉 ¡Felicidades Campeón! 🎉
                </h2>
                <p className="text-zinc-400 mb-6">
                  Ganaste el premio de <span className="text-amber-400 font-semibold capitalize">{formatMonthYear(prize.month_year)}</span>
                </p>

                {/* Prize Card */}
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/30 rounded-xl p-5 mb-6">
                  <Gift className="h-8 w-8 text-amber-400 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">{prize.prize_name}</h3>
                  {prize.prize_description && (
                    <p className="text-sm text-zinc-300">{prize.prize_description}</p>
                  )}
                </div>

                <Button
                  onClick={handleClaim}
                  disabled={claiming}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-lg py-6"
                >
                  {claiming ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      >
                        <Gift className="h-5 w-5 mr-2" />
                      </motion.div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Gift className="h-5 w-5 mr-2" />
                      Reclamar Mi Premio
                    </>
                  )}
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-6"
              >
                {/* Success Animation */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                  className="inline-block mb-6"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-green-500/30 blur-2xl rounded-full" />
                    <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-full p-4 relative z-10">
                      <PartyPopper className="h-12 w-12 text-white" />
                    </div>
                  </div>
                </motion.div>

                <h2 className="text-2xl font-bold text-white mb-2">
                  ¡Premio Reclamado!
                </h2>
                <p className="text-zinc-400 mb-6">
                  Presenta este código en la tienda para recibir tu premio
                </p>

                {/* Claim Code */}
                <div 
                  onClick={handleCopy}
                  className="bg-black/40 border-2 border-dashed border-amber-500/50 rounded-xl p-4 mb-6 cursor-pointer hover:bg-black/60 transition-colors"
                >
                  <p className="text-xs text-zinc-500 mb-1">CÓDIGO DE RECLAMO</p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-2xl font-mono font-bold text-amber-400 tracking-wider">
                      {claimCode}
                    </span>
                    {copied ? (
                      <Check className="h-5 w-5 text-green-400" />
                    ) : (
                      <Copy className="h-5 w-5 text-zinc-500" />
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">Toca para copiar</p>
                </div>

                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Cerrar
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </>
  );
}
